import React, { FormEvent, useEffect, useState } from 'react'
import { IProgramFunction } from '../../../types/program_function';
import useUpdateProgramFunction from '../../../hooks/program-function/useUpdateProgramFunction';
import useCreateProgramFunction from '../../../hooks/program-function/useCreateProgramFunction';
import useDeleteProgramFunction from '../../../hooks/program-function/useDeleteProgramFunction';
import { IMetaFunction } from '../../../types/meta_function';
import useDuplicateProgramFunction from '../../../hooks/program-function/useDuplicateProgramFunction';

interface IProgramFunctionProps {
    programFunction: IProgramFunction;
    currOption: number[];
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isDelete: boolean;
    setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    metaFunctions: IMetaFunction[];
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
    setIsAction: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    isAction: boolean;
    isCreateDuplicate: boolean;
    setDuplicateModalText: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
    setCreateDuplicateModal: React.Dispatch<React.SetStateAction<boolean>>;
    setCreateDuplicate: React.Dispatch<React.SetStateAction<boolean>>;

}

const ProgramFunction = ({ isCreateDuplicate, programFunction, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, metaFunctions, setIsResetNew, setDeleteModalMessage, setIsAction, isAction, setIsOpenModal, setCreateDuplicateModal, setCreateDuplicate, setDuplicateModalText }: IProgramFunctionProps) => {
    const [title, setTitle] = useState<string>(programFunction.id ? programFunction.name : 'New Program Function');

    const [currentProgramFunction, setCurrentProgramFunction] = useState<IProgramFunction>(programFunction);


    const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
    const [, setIsServiceError] = useState<string | null>(null); //eslint-disable-line

    useEffect(() => {
        setCurrentProgramFunction(programFunction);
        setTitle(programFunction.id ? programFunction.name : 'New Program Function');
    }, [programFunction]);


    useEffect(() => {
        if (!programFunction.id) return;
        if (isCreateDuplicate && programFunction?.id) {
            duplicateProgramFunction(programFunction.id);
            setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3]]);
            setCreateDuplicate(false)
        }
    }, [programFunction?.id, isCreateDuplicate]) //eslint-disable-line

    const { mutate: updateProgramFunction } = useUpdateProgramFunction({ setIsProcessRunning, setIsServiceError });
    const { mutate: createProgramFunction } = useCreateProgramFunction({ setIsProcessRunning, setIsServiceError });
    const { mutate: deleteProgramFunction } = useDeleteProgramFunction();
    const { mutate: duplicateProgramFunction } = useDuplicateProgramFunction({ setIsProcessRunning, setIsServiceError });

    useEffect(() => {
        if (isDelete) {
            if (currOption.length - 1 === 3) {
                setCurrOption([currOption[0], currOption[1], currOption[2]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], 0]);
            }
            if (!programFunction.id) return;
            deleteProgramFunction(programFunction.id);
            setIsDelete(false);
        }
    }, [isDelete]); //eslint-disable-line

    useEffect(() => {
        if (isAction) {
            updateProgramFunction(currentProgramFunction);
            setIsOpenModal(false);
            setIsAction(false);
            setTitle(currentProgramFunction.name || 'New Program Function');
        }
    }, [isAction]); //eslint-disable-line

    if (!currentProgramFunction) {
        return <p>Loading...</p>
    }

    const handleSave = (e: FormEvent) => {
        e.preventDefault();

        if (!programFunction.id) {
            createProgramFunction(currentProgramFunction);
        } else {
            setIsOpenModal(true);
            setDeleteModalMessage({ title: 'Update Program Function', subtitle: 'This action will delete all the parameters of older function. Do you want to continue?' })
        }
    }

    const handleDelete = () => {
        if (!programFunction.id) {
            setIsResetNew(true);
            if (currOption.length - 1 === 3) {
                setCurrOption([currOption[0], currOption[1], currOption[2]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], 0]);
            }
        } else {
            setIsOpenDeleteModal(true);
            setDeleteModalMessage({ title: 'Delete Program Function', subtitle: 'This will delete all the parameters associated with the program function. Are you sure you want to delete?' });
        }
    }

    const handleMetaFunctionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMetaFunctionName = e.target.value;
        const newMetaFunction = metaFunctions.find(metaFunction => metaFunction.name === newMetaFunctionName);
        if (!newMetaFunction) return;
        const oldMetaFunction = metaFunctions.find(metaFunction => metaFunction.name === currentProgramFunction.name);

        setCurrentProgramFunction({
            ...currentProgramFunction,
            name: newMetaFunctionName,
            description: (!currentProgramFunction.description?.trim()?.length || currentProgramFunction.description === oldMetaFunction?.description)
                ? newMetaFunction.description
                : currentProgramFunction.description
        });
    }

    // const handleCreateDuplicateProgramFunction = () => {
    //     if (!programFunction.id) return;

    //     duplicateProgramFunction(programFunction.id);
    //     setCurrOption([currOption[0], currOption[1], currOption[2], currOption[3] + 1]);
    // }

    const openDuplicateModal = () => {
        setDuplicateModalText({ title: 'Create Duplicate Program Function', subtitle: 'This will create a duplicate of the program function. Do you want to continue?' });
        setCreateDuplicateModal(true);
    }


    return (
        <div className='imm_admin__content'>
            <div className='content_head'>
                <h3>{title}</h3>
                {programFunction.id && <button onClick={openDuplicateModal}>{isProcessRunning ? <i className='imm_admin__process_loader' /> : "Create duplicate"}  </button>}
            </div>

            <form onSubmit={handleSave} className='content_form'>
                <div className='field'>
                    <label htmlFor='meta_function'>Meta Function <span className='field__required'>*</span></label>
                    <select id='meta_function' name='meta_function' value={currentProgramFunction.name || -1} onChange={handleMetaFunctionChange} >
                        <option value={-1} disabled>Select meta function</option>
                        {metaFunctions.map(metaFunction => {
                            return <option key={metaFunction.name} value={metaFunction.name}>{metaFunction.name}</option>
                        })}
                    </select>
                </div>

                <div className='field'>
                    <label htmlFor='meta_description'>Meta Description</label>
                    <textarea id='meta_description' name='meta_description' disabled placeholder='Enter Description' value={metaFunctions?.find(metaFunction => metaFunction.name === currentProgramFunction.name)?.description || ""}></textarea>
                </div>

                <div className='field'>
                    <label htmlFor='description'>Description <span className='field__required'>*</span></label>
                    <textarea id='description' name='description' placeholder='Enter Description' value={currentProgramFunction.description || ''} onChange={(e) => setCurrentProgramFunction({ ...currentProgramFunction, description: e.target.value })}></textarea>
                </div>

                <div className='actions'>
                    <button className='delete_btn' type="button" onClick={handleDelete}>{!programFunction.id ? 'Cancel' : 'Delete'}</button>
                    <button className='save_btn' type='submit'>{isProcessRunning ? <i className='imm_admin__process_loader' /> : !programFunction.id ? 'Create' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
}

export default ProgramFunction;